/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/RecordLandingPage.tsx
 * =============================================================================
 * CHANGE LOG
 * | # | Date       | Who               | Why              | What Changed       |
 * |---|------------|-------------------|------------------|--------------------|
 * | 1 | 2026-06-22 | Claude Sonnet 4.6 | UX improvement   | Record landing page|
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * - apps/studio/src/App.tsx  (<RecordLandingPage />)
 *
 * EXPORTS
 * RecordLandingPage — shown on the Record tab when the phone is not connected
 * =============================================================================
 */

import { useState, useEffect } from "react";

// ── Unique feature cards ──────────────────────────────────────────────────────

interface Feature {
  icon: string;
  title: string;
  body: string;
  color: string;
}

const FEATURES: Feature[] = [
  {
    icon: "🔌",
    title: "Zero Install — Just Plug In",
    body: "Connect your Android phone via USB, open Chrome, and start testing. No driver installation, no desktop app, no configuration. No other automation tool does this.",
    color: "#22c55e",
  },
  {
    icon: "📱",
    title: "Test ANYTHING on Android",
    body: "If you can see it on screen, Mimiq can test it. System Settings, Downloads folder, Camera, Calculator, OEM apps — no package names, no developer consultation needed. Just tap what you see.",
    color: "#6366f1",
  },
  {
    icon: "🌐",
    title: "Test Mobile Websites Too",
    body: "Mimiq records taps on Chrome's mobile website just like any native app. No extra setup — the same recorder works on both native apps and mobile web pages.",
    color: "#22d3ee",
  },
  {
    icon: "🤖",
    title: "AI Healing — 100% Offline",
    body: "When an element changes, Mimiq heals the locator using a 23 MB ONNX model on your CPU. No API keys, no cloud calls, no token billing. Your test data never leaves your laptop.",
    color: "#f59e0b",
  },
  {
    icon: "♻",
    title: "One Format Everywhere",
    body: "Record in the browser, run from CLI, edit in no-code, generate from TypeScript SDK — they all share the exact same test file. No 'studio format vs. code format' gap.",
    color: "#a78bfa",
  },
  {
    icon: "🛡",
    title: "Candidate-List Locators",
    body: "Every recorded tap stores multiple fallback locators tried in order. If the first fails, the second is tried automatically — no complete test breakage from a single UI change.",
    color: "#f38ba8",
  },
  {
    icon: "⌨️",
    title: "Type From Your Keyboard",
    body: "In Drive mode, every key you press on your laptop is forwarded to the phone instantly. While recording, keystrokes auto-create a single TypeText step — no manual step adding.",
    color: "#89b4fa",
  },
  {
    icon: "♻",
    title: "Reusable Function Library",
    body: "Club common steps (e.g. 'Login as Admin') into named functions with parameters. Call them from any test. Mimiq is the only no-code mobile tool with a function library.",
    color: "#cba6f7",
  },
];

// ── Connection guide steps ────────────────────────────────────────────────────

const CONNECT_STEPS = [
  { n: "1", text: "Enable Developer Options on your phone: Settings → About Phone → tap Build Number 7 times" },
  { n: "2", text: "Enable USB Debugging: Developer Options → USB Debugging → ON" },
  { n: "3", text: "Connect your phone to this PC with a USB data cable" },
  { n: "4", text: "Click Connect Phone (top right) → select your phone in the Chrome dialog" },
  { n: "5", text: "On the phone: tap Allow USB debugging → the mirror appears in a few seconds" },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface RecordLandingPageProps {
  onConnect: () => void;
}

export function RecordLandingPage({ onConnect }: RecordLandingPageProps) {
  const [activeFeature, setActiveFeature] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-rotate features every 4 seconds
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setActiveFeature((i) => (i + 1) % FEATURES.length);
    }, 4000);
    return () => clearInterval(t);
  }, [paused]);

  const feat = FEATURES[activeFeature]!;

  return (
    <div className="rlp-root">
      {/* ── Left: Connect guide ── */}
      <div className="rlp-left">
        <div className="rlp-logo-row">
          <span className="rlp-logo">Mimiq</span>
          <span className="rlp-tagline">Plug in your phone. Start testing.</span>
        </div>

        <button className="rlp-connect-btn" onClick={onConnect}>
          <span className="rlp-connect-icon">🔗</span>
          Connect Phone
        </button>

        <div className="rlp-steps">
          <p className="rlp-steps-title">How to connect</p>
          {CONNECT_STEPS.map((s) => (
            <div key={s.n} className="rlp-step">
              <span className="rlp-step-num">{s.n}</span>
              <span className="rlp-step-text">{s.text}</span>
            </div>
          ))}
        </div>

        <div className="rlp-browser-note">
          ⚠ Chrome or Edge only — WebUSB is not available in Firefox or Safari
        </div>
      </div>

      {/* ── Right: Rotating feature showcase ── */}
      <div className="rlp-right">
        <p className="rlp-features-title">What makes Mimiq unique</p>

        {/* Active feature card */}
        <div
          className="rlp-feature-card"
          style={{ borderColor: feat.color, background: `${feat.color}0f` }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <span className="rlp-feature-icon">{feat.icon}</span>
          <div>
            <h3 className="rlp-feature-title" style={{ color: feat.color }}>{feat.title}</h3>
            <p className="rlp-feature-body">{feat.body}</p>
          </div>
        </div>

        {/* Dot indicators */}
        <div className="rlp-dots">
          {FEATURES.map((_, i) => (
            <button
              key={i}
              className={`rlp-dot${activeFeature === i ? " rlp-dot--active" : ""}`}
              style={activeFeature === i ? { background: feat.color } : undefined}
              onClick={() => { setActiveFeature(i); setPaused(true); setTimeout(() => setPaused(false), 8000); }}
              title={FEATURES[i]!.title}
            />
          ))}
        </div>

        {/* Mini feature grid */}
        <div className="rlp-mini-grid">
          {FEATURES.map((f, i) => (
            <button
              key={i}
              className={`rlp-mini-card${activeFeature === i ? " rlp-mini-card--active" : ""}`}
              onClick={() => { setActiveFeature(i); setPaused(true); setTimeout(() => setPaused(false), 8000); }}
              title={f.title}
            >
              <span className="rlp-mini-icon">{f.icon}</span>
              <span className="rlp-mini-label">{f.title.split(" — ")[0] ?? f.title}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
